import { animate, motion, useInView, useMotionValue, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaAndroid, FaApple, FaLaptopCode, FaRobot, FaUsers, FaRocket, FaStar, FaShieldAlt } from "react-icons/fa";
import { SiTwilio, SiFiverr, SiUpwork, SiFreelancer } from "react-icons/si";
import WhyUsParallax from "../Components/Perform";
import AnimatedBackground from "../Components/Background";
import { Link } from "react-router-dom";
import { FaMobileButton } from "react-icons/fa6";
import { TbMessageReportFilled } from "react-icons/tb";
import HeroVideoPlayer from "../Components/HeroVideoPlayer";
import HeroVideoFrame from "../Components/HeroVideoFrame";
import { LandingSections } from "../Components/LandingSections";
import TechnologyMarquee from "../Components/TechnologyMarquee";
import BrandButton from "../Components/BrandButton";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", delay },
  }),
};

const heroSlide = {
  hidden: { y: 20 },
  visible: (delay = 0) => ({
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay },
  }),
};

const GHLLogoIcon = ({ className = "w-10 h-10" }) => (
  <svg
    className={className}
    viewBox="0 0 64 64"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 23.5 21 9l13 14.5h-7.5V55h-11V23.5H8Z" fill="currentColor" opacity="0.72" />
    <path d="M30 23.5 43 9l13 14.5h-7.5V55h-11V23.5H30Z" fill="currentColor" opacity="0.86" />
    <path d="M19 36.5 32 22l13 14.5h-7.5V55h-11V36.5H19Z" fill="currentColor" />
    <path d="M21 9 8 23.5h7.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity="0.35" />
    <path d="M43 9 30 23.5h7.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity="0.35" />
    <path d="M32 22 19 36.5h7.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity="0.35" />
  </svg>
);

const cardData = [
  {
    icon: <FaRobot className="w-10 h-10" />,
    title: "AI Agents",
    desc: "Deploy conversational AI agents that understand natural language, handle tasks, and engage users across voice, chat, and web interfaces.",
    img: "/Wavy_Tech-21_Single-04.jpg", // AI-related image
    path: "/portfolio/ai-agents",
    accent: "#8B4AFA",
    tint: "#8a4afa1c",
  },
  {
    icon: <SiTwilio className="w-10 h-10" />,
    title: "Twilio IVRs",
    desc: "Implement intelligent IVR systems powered by Twilio - enabling seamless customer interactions, automated routing, and voice AI integration.",
    img: "/5143151.jpg", // AI-related image
    path: "/portfolio/twilio-ivrs",
    accent: "#06B6D4",
    tint: "#06b5d428",
  },
  {
    icon: <GHLLogoIcon className="w-10 h-10" />,
    title: "GoHighLevel Solutions",
    desc: "Streamline your business with GoHighLevel CRM, workflow automation, lead management, sales funnels, and appointment booking solutions.",
    img: "/logos/gohighlevel-new.webp",
    path: "/portfolio/ghl-integration",
    accent: "#DB2777",
    tint: "#ec489922",
    iconTint: "#fdf2f8",
    iconColor: "#EC4899",
  },
  {
    icon: <TbMessageReportFilled className="w-10 h-10" />,
    title: "A2P 10DLC APPROVAL",
    desc: "We handle the entire A2P registration process from brand verification to campaign submission ensuring 100% approval, with maximum message deliverability through Twilio.",
    img: "/20943399.jpg", // A2P-related image
    path: "/portfolio/a2p-approval",
    accent: "#34D399",
    tint: "#34d39931",
  },
  {
    icon: <FaLaptopCode className="w-10 h-10" />,
    title: "Web Applications",
    desc: "We build dynamic, responsive websites and full-stack web platforms optimized for performance, security, and user engagement.",
    img: "/20943849.jpg", // AI-related image
    path: "/portfolio/web-apps",
    accent: "#4A9AFA",
    tint: "#98c4ff81",
  },

  {
    icon: <FaMobileButton className="w-10 h-10" />,
    title: "Mobile Application",
    desc: "We craft high-performance, feature-rich Android/IOS applications tailored to your business needs - combining sleek UI with scalable architecture.",
    img: "/20943533.jpg", // AI-related image
    path: "/portfolio/mobile-apps",
    accent: "#F97316",
    tint: "#f974161c",
  },
];

const techStack = [
  { name: "Vue.js", logo: "/tech-icons/vue.webp" },
  { name: "MEAN Stack", logo: "/tech-icons/mean-stack.webp" },
  { name: "Python", logo: "/tech-icons/python.webp" },
  { name: "PrestaShop", logo: "/tech-icons/prestashop.webp" },
  { name: "Android", logo: "/tech-icons/android.webp" },
  { name: "iOS", logo: "/tech-icons/ios.webp" },
  { name: "Laravel", logo: "/tech-icons/laravel.webp" },
  { name: "React Native", logo: "/tech-icons/react-native.webp" },
  { name: "Unity", logo: "/tech-icons/unity.webp" },
  { name: "C++", logo: "/tech-icons/cplusplus.webp" },
  { name: "Twilio", logo: "/tech-icons/twilio.webp" },
  { name: "MERN Stack", logo: "/tech-icons/mern-stack.webp" },
  { name: "WordPress", logo: "/tech-icons/wordpress.webp" },
  { name: "WooCommerce", logo: "/tech-icons/woocommerce.webp" },
];

const AnimatedStatValue = ({
  value,
  suffix = "",
  decimals = 0,
  minWidth = "min-w-[4.5ch]",
}) => {
  const valueRef = useRef(null);
  const isInView = useInView(valueRef, { amount: 0.5, once: true });
  const count = useMotionValue(0);
  const prefersReducedMotion = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return undefined;

    if (prefersReducedMotion) {
      count.set(value);
      setDisplayValue(value);
      return undefined;
    }

    const controls = animate(count, value, {
      duration: 1.5,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: setDisplayValue,
    });

    return () => controls.stop();
  }, [count, isInView, prefersReducedMotion, value]);

  const formattedValue = decimals
    ? displayValue.toFixed(decimals)
    : Math.round(displayValue).toLocaleString("en-US");

  return (
    <span ref={valueRef} className={`inline-block tabular-nums ${minWidth}`}>
      {formattedValue}
      {suffix}
    </span>
  );
};

const HeroProof = () => {
  const stats = [
    {
      icon: <FaUsers />,
      value: <AnimatedStatValue value={1000} suffix="+" minWidth="min-w-0" />,
      label: "Happy Clients",
    },
    {
      icon: <FaRocket />,
      value: <AnimatedStatValue value={3000} suffix="+" minWidth="min-w-0" />,
      label: "Projects Completed",
    },
    {
      icon: <FaStar />,
      value: (
        <>
          <AnimatedStatValue value={5} decimals={1} minWidth="min-w-0" />
          <span
            aria-hidden="true"
            className="ml-2 align-middle text-sm tracking-[0.08em] text-amber-400"
          >
            ★★★★★
          </span>
        </>
      ),
      label: "Average Rating",
    },
    {
      icon: <FaShieldAlt />,
      value: "Top Rated",
      label: "Fiverr · Upwork · Freelancer",
    },
  ];

  return (
    <div className="relative z-10 mt-10 border-t border-slate-200/70 pt-6 lg:mt-14 lg:pt-8">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-7 lg:grid-cols-4 lg:gap-x-0">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex min-w-0 flex-col-reverse lg:border-l lg:border-slate-200/70 lg:pl-8 lg:first:border-l-0 lg:first:pl-0"
          >
            {/* Label (shown second because of flex-col-reverse) */}
            <dt className="flex items-center gap-2 text-xs font-medium leading-snug text-slate-600 sm:text-sm">
              <span aria-hidden="true" className="shrink-0 text-[13px] text-[#8B4AFA]/80">
                {stat.icon}
              </span>
              <span className="min-w-0">{stat.label}</span>
            </dt>

            {/* Value (shown first) */}
            <dd className="mb-1.5 font-display text-xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-2xl">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const Home = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const order1 = [
    [0, 1, 2],
    [3, 4, 5],
  ];
  const order2 = [[0], [1], [2], [3], [4], [5]];
  const carouselRef = null;
  const scrollRef = null;
  const scrollRef2 = null;
  const scrollRef3 = null;

  return (
    <section>
      <AnimatedBackground className="p-2" />
        {/* Hero Section */}
<section
  id="home"
  className="mk-home-hero relative isolate overflow-hidden pb-14 pt-40 sm:pt-44 lg:pb-16 lg:pt-[11.25rem]"
>
  <div aria-hidden="true" className="mk-home-hero-atmosphere pointer-events-none absolute inset-0" />
  <div aria-hidden="true" className="grid-overlay mk-home-hero-grid" />

  <div className="relative mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-4">
  <div className="relative z-10 grid items-center gap-12 lg:grid-cols-[minmax(0,0.93fr)_minmax(0,1.07fr)] lg:gap-10">
      <div className="min-w-0 max-w-2xl text-left">
        {/* Eyebrow pill */}
        <motion.p
          className="inline-flex items-center gap-2 rounded-md border border-[#8B4AFA]/20 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#6e3fc7]"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#8B4AFA]" />
          MK AI TECHNOLOGIES
        </motion.p>

        {/* Heading: larger */}
        <motion.h1
          className="mt-5 font-display text-4xl font-semibold leading-[1.04] text-slate-900 sm:text-5xl lg:text-[2.6rem] xl:text-[3.2rem] 2xl:text-[3.6rem] lg:tracking-[-0.03em]"
          variants={heroSlide}
          initial="hidden"
          animate="visible"
          custom={0.1}
        >
          <span className="lg:block xl:whitespace-nowrap">AI Solutions &amp; Business</span>{" "}
         <span className="lg:block xl:whitespace-nowrap">
            <motion.span
              className="inline-block bg-gradient-to-r from-[#7C3AED] via-[#8B4AFA] to-[#C026D3] bg-clip-text pb-1 text-transparent"
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Automation
                </motion.span>{" "}
                That Scales
          </span>{" "}
         <span className="lg:block xl:whitespace-nowrap">With Confidence</span>
        </motion.h1>

        {/* Subheading: larger and darker */}
        <motion.p
          className="mt-5 max-w-xl text-lg leading-[1.6] text-slate-700 xl:text-xl"
          variants={heroSlide}
          initial="hidden"
          animate="visible"
          custom={0.2}
        >
          From AI voice agents and Twilio IVR to A2P 10DLC registration, GoHighLevel setup, and
          custom web and mobile apps, we help growing businesses automate, communicate, and scale.
        </motion.p>

        {/* Buttons: unchanged */}
        <motion.div
          className="mt-6 grid max-w-[34rem] grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.4}
        >
         <BrandButton
            as={Link}
            to="/contact"
            navbar
            className="mk-cta-button--full"
          >
            Share Your Requirements
          </BrandButton>

          <BrandButton
              as="a"
              href="https://agent.mkaitechnology.com/"
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              className="mk-cta-button--full bg-white/80"
            >
              Test Our AI Agent
            </BrandButton>
        </motion.div>
      </div>

      {/* Video */}
      <motion.div
        className="mk-hero-video-stage relative isolate w-full"
        initial={{ opacity: 0, x: 28 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-[#8B4AFA]/25 via-fuchsia-300/10 to-transparent blur-3xl"
        />
        <HeroVideoFrame>
          <HeroVideoPlayer />
        </HeroVideoFrame>
      </motion.div>
    </div>

    <HeroProof />
  </div>
</section>

      <TechnologyMarquee technologies={techStack} />

      <LandingSections
        services={cardData}
        processSection={<WhyUsParallax />}
      />

      {false && (
        <>
      {/* Divider after hero */}
      {/* <motion.div
        style={{
          scaleX,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          originX: 0,
          backgroundColor: "#8B4AFA",
          opacity: 0.3,
          zIndex: 0,
        }}
      /> */}

      {/* Expertise Section */}
      <div
        ref={scrollRef}
        className="mx-auto mt-20 max-w-[84rem] overflow-y-hidden"
      >
        <div className="border-b border-dotted border-[#8B4AFA] mx-auto max-w-2xl text-md text-center tracking-widest text-slate-700 sm:mt-6">
          Our Areas of Expertise
        </div>
        <div className="mx-auto text-center mt-4 max-w-4xl font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-7xl leading-tight">
          Explore Our Digital Skillset
        </div>
        <div className="mt-[5%] flex flex-col gap-[10px] md:block hidden">
          {order1.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-3">
              {row.map((index) => {
                const { icon, title, desc, img, path } = cardData[index];
                return (
                  <motion.div
                    key={index}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    animate={{
                      flex:
                        hoveredIndex === index
                          ? 1.6
                          : hoveredIndex !== null && row.includes(hoveredIndex)
                          ? 0.9
                          : 1.2,
                    }}
                    transition={{ duration: 0.3 }}
                    className="border bg-white rounded-xl shadow-md cursor-pointer text-black flex flex-col justify-start items-start overflow-hidden"
                  >
                    <Link to={path} className="w-full h-full flex flex-col">
                      <div className="p-3 w-full bg-[#8B4AFA] text-white">
                        {icon}
                      </div>
                      <div className="p-4 flex flex-col">
                        <h3 className="font-semibold text-base">{title}</h3>
                        <img src={img} alt={title} className="w-1/2 object-cover mt-auto" />
                        <p className="text-sm mt-1 opacity-90">{desc}</p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Mobile Expertise Section */}
        <div className="mt-[5%] flex flex-col gap-[10px]  block md:hidden overflow-hidden px-4">
          {order2.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 flex-wrap">
              {row.map((index) => {
                const { icon, title, desc, img, path } = cardData[index];
                return (
                    <motion.div
                      key={index}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      animate={{
                        flex:
                          hoveredIndex === index
                            ? 1.6
                            : hoveredIndex !== null && row.includes(hoveredIndex)
                            ? 0.9
                            : 1.2,
                      }}
                      transition={{ duration: 0.3 }}
                      className="border bg-white rounded-xl shadow-md cursor-pointer text-black flex flex-col justify-start items-start overflow-hidden"
                    >
                      <Link to={path} className="w-full h-full flex flex-col">
                        <div className="p-3 w-full bg-[#8B4AFA] text-white">
                          {icon}
                        </div>
                        <div className="p-4 flex flex-col">
                          <h3 className="font-semibold text-base">{title}</h3>
                          <img src={img} alt={title} className="w-1/2 object-cover mt-auto" />
                          <p className="text-sm mt-1 opacity-90">{desc}</p>
                        </div>
                      </Link>
                    </motion.div>
                  );
              })}
            </div>
          ))}
        </div>
        {/* */}
      </div>

      {/* Divider after Expertise */}
      {/* <motion.div
        className="mt-[8%]"
        style={{
          scaleX,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          originX: 1,
          backgroundColor: "#8B4AFA",
          opacity: 0.3,
          zIndex: 0,
        }}
      /> */}

      {/* Why Us Section */}
      <div
        ref={scrollRef2}
        className="mx-auto mt-20 max-w-[84rem] overflow-y-hidden"
      >
        <WhyUsParallax />
      </div>

      {/* Divider after Why Us */}
      {/* <motion.div
        className="mt-[8%]"
        style={{
          scaleX: scaleX2,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          originX: 0,
          backgroundColor: "#8B4AFA",
          opacity: 0.3,
          zIndex: 0,
        }}
      /> */}
      <div
        ref={scrollRef3}
        className="mx-auto mt-20 max-w-[84rem] overflow-y-hidden"
      >
        {" "}
        <div className="border-b border-dotted border-[#8B4AFA] mx-auto max-w-2xl text-md text-center tracking-widest text-slate-700 sm:mt-6">
          Our Technology Stack
        </div>
        <div className="mx-auto text-center mt-4 max-w-4xl font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-6xl leading-tight">
          Technologies We Use
        </div>
        <div className="overflow-hidden mt-12 px-4">
          {/* Show draggable carousel only on md+ */}
          <div className="hidden md:block">
            <motion.div
              ref={carouselRef}
              className="flex gap-6 cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: -1100, right: 2 }}
              whileTap={{ cursor: "grabbing" }}
            >
              {techStack.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  className="min-w-[140px] h-36 bg-white border rounded-lg shadow-md flex flex-col items-center justify-center p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    className="w-12 h-12 object-contain mb-2"
                  />
                  <p className="text-sm font-medium text-gray-700">
                    {tech.name}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* On small screens show static grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:hidden">
            {techStack.map((tech, index) => (
              <div
                key={tech.name}
                className="h-36 bg-white border rounded-lg shadow-md flex flex-col items-center justify-center p-4"
              >
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="w-12 h-12 object-contain mb-2"
                />
                <p className="text-sm font-medium text-gray-700">{tech.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* <motion.div
        className="mt-[8%]"
        style={{
          scaleX: scaleX3,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          originX: 1,
          backgroundColor: "#8B4AFA",
          opacity: 0.3,
          zIndex: 0,
        }}
      /> */}
      <div className="mx-auto mt-20 max-w-[84rem] overflow-y-hidden">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center max-w-4xl mx-auto my-20 px-4"
        >
          <div className="border-b border-dotted border-[#8B4AFA] mx-auto max-w-2xl text-md text-center tracking-widest text-slate-700 sm:mt-6">
            Get Consultation
          </div>

          <h2 className="mx-auto text-center mt-4 max-w-4xl font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-6xl leading-tight">
            We Are the Decisive Factor Behind Your Success
          </h2>

          <p className="mt-6 text-lg text-gray-600">
            Empowering Businesses with Modern Technology
          </p>

          <p className="mt-4 text-md text-gray-500 max-w-2xl mx-auto">
            Our expert engineers combine creativity, performance, and innovation
            to deliver scalable solutions across modern technologies - ensuring
            your business thrives in the digital landscape. Whether you're
            starting from scratch or scaling globally, our stack is built for
            results.
          </p>

          <div className="mt-8">
            <BrandButton as={Link} to="/contact">
              Get Consultation
            </BrandButton>
          </div>
        </motion.div>
      </div>
        </>
      )}
    </section>
  );
};

export default Home;
